// Pure decision for what to do after a paid submission's charge has (or has not)
// gone through. Extracted from photo-gallery.tsx `castVote`/`onCastSubmission`,
// where two failure paths silently dropped the nomination after taking money:
//   - PURCHASE-F_06: buildVote returned null (missing voter/ballot/group/question
//     ids) -> castVote(null) logged and returned, no submission, no user notice.
//   - PURCHASE-F_07: addVote rejected -> .catch logged and swallowed, success
//     step still shown.
// The fix: on either failure after a successful charge, surface an error, do NOT
// advance to the success step, and flag the case for reconciliation
// (money-taken-no-vote) instead of failing silently.

export interface SubmissionOutcomeInput {
  /** the vote/submission payload buildVote produced (null if it could not be built) */
  voteObject: unknown | null
  /** whether we have an authenticated voter to attribute the vote to */
  voterPresent: boolean
  /** whether the Stripe charge succeeded for this attempt (false for free ballots) */
  chargeSucceeded: boolean
  /** whether the addVote backend call rejected */
  addVoteError?: boolean
}

export interface SubmissionOutcomeResult {
  /** the vote was (or will be) persisted */
  recorded: boolean
  /** surface a message to the user instead of failing silently */
  alertUser: boolean
  /** advance the wizard to the "Demonstrate success" step */
  showSuccessStep: boolean
  /** money was captured but no vote exists -> needs manual/automated reconciliation */
  needsReconciliation: boolean
}

const dropped = (chargeSucceeded: boolean): SubmissionOutcomeResult => ({
  recorded: false,
  alertUser: true,
  showSuccessStep: false,
  needsReconciliation: chargeSucceeded,
})

/**
 * Decide the post-payment submission outcome. Pure: safe to unit test and to
 * call from the gallery's castVote flow.
 */
export const resolveSubmissionOutcome = (
  input: SubmissionOutcomeInput,
): SubmissionOutcomeResult => {
  // F_06: no vote could be built (missing identity) or no voter to attribute to.
  if (input.voteObject === null || input.voteObject === undefined || !input.voterPresent) {
    return dropped(input.chargeSucceeded)
  }
  // F_07: the persistence call failed.
  if (input.addVoteError) {
    return dropped(input.chargeSucceeded)
  }
  return {
    recorded: true,
    alertUser: false,
    showSuccessStep: true,
    needsReconciliation: false,
  }
}
